import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslate } from 'src/locales';
import { COMPARISON_ROWS, ComparisonRowKey } from 'src/services/presentations/presentations.types';

import Iconify from 'src/components/iconify';

/** Every row in the order shown, and the ones left out. */
export interface ComparisonRowsValue {
  order: ComparisonRowKey[];
  off: ComparisonRowKey[];
}

/** The given rows ticked in their order, then the rest unticked. Without any, every row is ticked. */
export function rowsLayout(rows?: readonly ComparisonRowKey[] | null): ComparisonRowsValue {
  const known = (rows ?? []).filter((row, index, all) => COMPARISON_ROWS.includes(row) && all.indexOf(row) === index);
  const on = known.length ? known : [...COMPARISON_ROWS];
  const off = COMPARISON_ROWS.filter((row) => !on.includes(row));
  return { order: [...on, ...off], off };
}

/** The ticked rows, in order. */
export const rowsOn = ({ order, off }: ComparisonRowsValue) => order.filter((row) => !off.includes(row));

/**
 * A comparison's rows (JT1-1887, C6): tick the ones to show and use the arrows to order them. Used
 * when making a comparison, and on Settings for the rows a new one starts with.
 */
export default function ComparisonRowsPicker({ value, onChange }: { value: ComparisonRowsValue; onChange: (next: ComparisonRowsValue) => void }) {
  const { t } = useTranslate();
  const { order, off } = value;

  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ order: next, off });
  };

  const toggle = (row: ComparisonRowKey) => {
    onChange({ order, off: off.includes(row) ? off.filter((other) => other !== row) : [...off, row] });
  };

  return (
    <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
      {order.map((row, index) => {
        const label = t(`presentations.packs.row_labels.${row}`);
        return (
          <ListItem
            key={row}
            sx={{ pr: 11 }}
            secondaryAction={(
              <>
                <IconButton size="small" aria-label={t('presentations.packs.row_up', { row: label })} disabled={index === 0} onClick={() => move(index, -1)}>
                  <Iconify icon="eva:arrow-upward-fill" width={18} />
                </IconButton>
                <IconButton size="small" aria-label={t('presentations.packs.row_down', { row: label })} disabled={index === order.length - 1} onClick={() => move(index, 1)}>
                  <Iconify icon="eva:arrow-downward-fill" width={18} />
                </IconButton>
              </>
            )}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={<Checkbox size="small" checked={!off.includes(row)} onChange={() => toggle(row)} />}
              label={<Typography variant="body2">{label}</Typography>}
            />
          </ListItem>
        );
      })}
    </List>
  );
}
